import type { FC, ReactNode } from 'react';
import { SearchLg } from '@untitledui/icons';
import {
  Button as AriaButton,
  DialogTrigger,
  Popover
} from 'react-aria-components';
import { Avatar, getInitialsFromName } from '../../base/avatar/avatar';
import { Input } from '../../base/input/input';
import { cx } from '../../../utils/cx';
import { MobileNavigationHeader } from './base-components/mobile-header';
import {
  NavAccountCard,
  NavAccountMenu
} from './base-components/nav-account-card';
import { NavItemBase } from './base-components/nav-item';
import { NavList } from './base-components/nav-list';
import { Button } from '../../base/buttons/button';
import type { NavAccountItem } from './base-components/types';

export type NavItem = {
  /** Label text for the nav item. */
  label: string;
  /** URL to navigate to when the nav item is clicked. */
  href: string;
  /** Whether the nav item is currently active. */
  current?: boolean;
  /** Icon component to display. */
  icon?: FC<{ className?: string }>;
  /** Badge to display. */
  badge?: ReactNode;
  visibleOn: 'desktop' | 'mobile' | 'both';
  /** List of sub-items to display. */
  items?: NavItem[];
  onClick?: (e: React.MouseEvent) => void;
};

type Account = {
  id: string;
  firstName: string;
  lastName: string;
  profile?: {
    avatarUrl?: string | null;
  } | null;
};

export interface HeaderNavigationBaseProps {
  account?: Account | null;
  logoUrl?: string;
  loginUrl: string;
  signUpUrl: string;
  onSignOut?: (e: React.FormEvent) => void;
  /** URL of the currently active item. */
  activeUrl?: string;
  /** List of items to display. */
  items: NavItem[];
  /** List of sub-items to display. */
  subItems?: NavItem[];
  /** List of account items to display. */
  navAccountItems?: NavAccountItem[];
  /** Content to display in the trailing position. */
  trailingContent?: ReactNode;
  /** Whether to show the avatar dropdown. */
  showAvatarDropdown?: boolean;
  /** Whether to show the sidebar account menu. */
  showSidebarAccountMenu?: boolean;
  /** Whether to show the switch account in the nav account menu. */
  showMultipleAccountMenu?: boolean;
  /** Whether to hide the bottom border. */
  hideBorder?: boolean;
  link?: React.ElementType;
}

export const HeaderNavigationBase = ({
  account,
  logoUrl,
  loginUrl,
  signUpUrl,
  onSignOut = (e) => {
    e.preventDefault();
  },
  // activeUrl,
  items,
  subItems,
  navAccountItems,
  trailingContent,
  showAvatarDropdown = true,
  showSidebarAccountMenu = false,
  showMultipleAccountMenu = false,
  hideBorder = false,
  link: Link = 'a'
}: HeaderNavigationBaseProps) => {
  const activeSubNavItems =
    subItems ||
    items.find((item) => item.current && item.items && item.items.length > 0)
      ?.items;

  const showSecondaryNav = activeSubNavItems && activeSubNavItems.length > 0;
  const fullName = account
    ? `${account.firstName} ${account.lastName}`
    : undefined;
  const initials = getInitialsFromName(fullName);
  return (
    <>
      <MobileNavigationHeader logoUrl={logoUrl}>
        <aside className="flex h-full max-w-full flex-col justify-between overflow-auto border-r border-secondary bg-primary pt-4 lg:pt-6">
          <div className="flex flex-col gap-5 px-4 lg:px-5">
            {!!logoUrl && <img src={logoUrl} alt="Logo" className="h-12" />}
          </div>
          <div className="flex-1 flex flex-col justify-between">
            <NavList
              items={items.filter((item) => item.visibleOn !== 'desktop')}
            />
            <div>
              <div className="px-2">
                {!account && (
                  <>
                    <NavItemBase href={loginUrl} type="link">
                      Login
                    </NavItemBase>
                    <NavItemBase href={signUpUrl} type="link">
                      Sign Up
                    </NavItemBase>
                  </>
                )}
              </div>
              {!!account && !showSidebarAccountMenu && (
                <form onSubmit={onSignOut} className="px-2 pb-2">
                  <Button
                    className="justify-start w-full px-3"
                    color="tertiary"
                    size="md"
                    type="submit"
                  >
                    Sign out
                  </Button>
                </form>
              )}
              {!!account && showSidebarAccountMenu && (
                <div className="px-2 pb-4">
                  <NavAccountCard
                    showMultipleAccountMenu={showMultipleAccountMenu}
                    selectedAccountId={account?.id || ''}
                    accounts={[
                      {
                        id: account.id || '',
                        name: `${account.firstName || ''} ${account.lastName || ''}`,
                        avatar: account.profile?.avatarUrl || undefined,
                        initials
                      }
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </aside>
      </MobileNavigationHeader>

      <header className="max-lg:hidden">
        <section
          className={cx(
            'flex h-16 w-full items-center justify-center bg-primary md:h-18',
            (!hideBorder || showSecondaryNav) && 'border-b border-secondary'
          )}
        >
          <div className="flex w-full max-w-container justify-between pr-3 pl-4 md:px-8">
            <div className="flex flex-1 items-center">
              {!!logoUrl && (
                <Link
                  aria-label="Go to homepage"
                  href="/"
                  className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  {!!logoUrl && (
                    <img src={logoUrl} alt="Logo" className="h-12" />
                  )}
                </Link>
              )}

              <nav className="flex flex-1">
                <ul className="flex flex-1 justify-center gap-0.5">
                  {items
                    .filter((item) => item.visibleOn !== 'mobile')
                    .map((item) => (
                      <li key={item.label} className="py-0.5">
                        <NavItemBase
                          icon={item.icon}
                          href={item.href}
                          current={item.current}
                          badge={item.badge}
                          onClick={(e) => item.onClick?.(e)}
                          type="link"
                        >
                          {item.label}
                        </NavItemBase>
                      </li>
                    ))}
                </ul>
                {!account && (
                  <div className="flex gap-0.5">
                    <Button
                      href={loginUrl}
                      className="min-w-24"
                      color="tertiary"
                      size="md"
                    >
                      Login
                    </Button>
                    <Button
                      href={signUpUrl}
                      className="min-w-24"
                      color="secondary"
                      size="md"
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              {trailingContent}

              {!!account && showAvatarDropdown && (
                <DialogTrigger>
                  <AriaButton
                    className={({ isPressed, isFocused }) =>
                      cx(
                        'group relative inline-flex cursor-pointer',
                        (isPressed || isFocused) &&
                          'rounded-full outline-2 outline-offset-2 outline-focus-ring'
                      )
                    }
                  >
                    <Avatar
                      alt={`${account?.firstName} ${account?.lastName}`}
                      initials={initials}
                      src={account?.profile?.avatarUrl}
                      size="md"
                    />
                  </AriaButton>
                  <Popover
                    placement="bottom right"
                    offset={8}
                    className={({ isEntering, isExiting }) =>
                      cx(
                        'will-change-transform',
                        isEntering &&
                          'duration-300 ease-out animate-in fade-in placement-right:slide-in-from-left-2 placement-top:slide-in-from-bottom-2 placement-bottom:slide-in-from-top-2',
                        isExiting &&
                          'duration-150 ease-in animate-out fade-out placement-right:slide-out-to-left-2 placement-top:slide-out-to-bottom-2 placement-bottom:slide-out-to-top-2'
                      )
                    }
                  >
                    <NavAccountMenu
                      link={Link}
                      showMultipleAccountMenu={showMultipleAccountMenu}
                      selectedAccountId={account.id}
                      onSignOut={onSignOut}
                      items={navAccountItems}
                      accounts={[
                        {
                          id: account.id || '',
                          name: `${account.firstName || ''} ${account.lastName || ''}`,
                          avatar: account.profile?.avatarUrl || undefined,
                          initials
                        }
                      ]}
                    />
                  </Popover>
                </DialogTrigger>
              )}
            </div>
          </div>
        </section>

        {showSecondaryNav && (
          <section
            className={cx(
              'flex h-16 w-full items-center justify-center bg-primary',
              !hideBorder && 'border-b border-secondary'
            )}
          >
            <div className="flex w-full max-w-container items-center justify-between gap-8 px-8">
              <nav>
                <ul className="flex items-center gap-0.5">
                  {activeSubNavItems.map((item) => (
                    <li key={item.label} className="py-0.5">
                      <NavItemBase
                        icon={item.icon}
                        href={item.href}
                        current={item.current}
                        badge={item.badge}
                        type="link"
                      >
                        {item.label}
                      </NavItemBase>
                    </li>
                  ))}
                </ul>
              </nav>

              <Input
                shortcut
                aria-label="Search"
                placeholder="Search"
                icon={SearchLg}
                size="sm"
                className="max-w-xs"
              />
            </div>
          </section>
        )}
      </header>
    </>
  );
};
